import Icon from "@/Components/Icon/Icon";
import Spinner from "@/Components/Spinner/Spinner";
import useCategoryQuery from "@/shared/hooks/useCategoryQuery";
import useFranchiseHook from "@/shared/hooks/useFranchiseHook";
import useGenerateImageUrl from "@/shared/hooks/useGenerateImageUrl";
import useKioskSettingsQuery from "@/shared/queries/kioskSettings/useKioskSettingsQuery";
import { DeliveryMethod } from "@/shared/utils/constant";
import { withCloudinaryBackgroundRemoval } from "@/shared/utils/helpers";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TakeawayImage from "../../../App/Assets/Images/menu.png";
import PackageImage from "../../../App/Assets/Images/paperbag.png";
import {
  DEFAULT_IMAGE_ID,
  imageType,
} from "../Settings/WebsiteSettings/Settings";
import { getKioskCategoriesWithProducts } from "./useKioskCategory";
import usePreloadKioskCategoryImages from "./usePreloadKioskCategoryImages";

const images = {
  9: PackageImage,
  3: TakeawayImage,
};

const KioskDeliveryMethod = ({ value, translationKey }: DeliveryMethod) => {
  const { data, isPending, isError, refetch } = useCategoryQuery(`${value}`);
  usePreloadKioskCategoryImages(data);
  const [franchiseId] = useFranchiseHook();
  const kioskSettingsQuery = useKioskSettingsQuery();
  const { t } = useTranslation();
  const generateImageUrl = useGenerateImageUrl();

  const categoryId = getKioskCategoriesWithProducts(data)[0]?.item_id;
  const isLoading = isPending || kioskSettingsQuery.isPending;
  const isDisabled =
    isLoading || isError || kioskSettingsQuery.isError || categoryId == null;

  const settingImage =
    value === 9
      ? kioskSettingsQuery.data?.take_out_image
      : value === 3
        ? kioskSettingsQuery.data?.dine_in_image
        : undefined;

  const resolvedImage =
    settingImage && settingImage !== DEFAULT_IMAGE_ID
      ? generateImageUrl(settingImage, imageType.SLIDER, franchiseId)
      : images[value];

  useEffect(() => {
    refetch({ cancelRefetch: false });
  }, []);

  const card = (
    <div className="relative flex h-[min(55vh,45rem)] w-[min(28vw,35rem)] flex-col items-center justify-center rounded-[clamp(1.5rem,2vw,3rem)] bg-(--card_background_color) p-[clamp(2rem,3vw,4rem)]">
      {isLoading && (
        <div className="absolute top-[clamp(1rem,1.5vw,1.5rem)] right-[clamp(1rem,1.5vw,1.5rem)]">
          <Spinner size="0.2rem" color="var(--card_text_color)" />
        </div>
      )}

      {isError && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[clamp(1.5rem,2vw,3rem)] bg-black/25 p-[clamp(1rem,2vw,2rem)]">
          <Icon
            variants="times"
            className="h-full w-full fill-error drop-shadow-lg"
          />
        </div>
      )}

      <img
        src={withCloudinaryBackgroundRemoval(resolvedImage)}
        className="h-[55%] w-full object-contain"
      />
      <div
        className="mt-[clamp(1.5rem,3vh,3rem)] text-[clamp(1.75rem,3vw,4rem)] text-(--card_text_color)"
        style={{ fontWeight: "900" }}
      >
        {t(`kioskPage.${translationKey}`)}
      </div>
    </div>
  );

  if (isDisabled) {
    return card;
  }

  return <Link to={`/kiosk/menu/${value}/${categoryId}`}>{card}</Link>;
};

export default KioskDeliveryMethod;
